import { useState, useCallback } from 'react';

import { useLocale } from '../../context/LocaleContext';
import { getRegexFromRestrictions } from '../../utilities/getRegexFromRestrictions';

/**
 * Custom hook for managing personalisation text state.
 * @param {Object} options - Configuration options.
 * @param {boolean} [options.enabledProp] - Controlled enabled state.
 * @param {string} [options.textProp] - Controlled text value.
 * @param {Function} [options.onToggle] - Callback for toggle changes.
 * @param {Function} [options.onTextChange] - Callback for text changes.
 * @returns {Object} State and handlers for personalisation text.
 */
export function useViewModel({ enabledProp, textProp, onToggle, onTextChange }) {
  const [enabledState, setEnabledState] = useState(!!enabledProp);
  const [textState, setTextState] = useState(textProp || '');
  const { formatPrice } = useLocale();

  const controlledEnabled = enabledProp !== undefined;
  const enabled = controlledEnabled ? enabledProp : enabledState;

  const controlledText = textProp !== undefined;
  const text = controlledText ? textProp : textState;

  // Default restrictions for gift wrap personalisation text
  // Allow letters, numbers, spaces, but limit symbols to basic punctuation only
  const defaultRestrictions = {
    allowLetters: true,
    allowNumbers: true,
    allowSpaces: true,
    allowSymbols: false, // Disable most symbols
    allowedTextCase: 'MIXED'
  };

  /**
   * Handles toggle state changes.
   * @param {boolean} next - The next enabled state.
   */
  const handleToggle = (next) => {
    if (!controlledEnabled) setEnabledState(next);
    if (typeof onToggle === 'function') onToggle(next);
  };

  /**
   * Handles text value changes with character filtering.
   * @param {string} val - The new text value.
   */
  const handleChange = useCallback(
    (val) => {
      // Get the regex pattern for allowed characters
      const allowedPattern = getRegexFromRestrictions(defaultRestrictions);

      // Create regex to match only allowed characters
      // eslint-disable-next-line
      const allowedRegex = new RegExp(`[${allowedPattern}]`, 'g');

      // Filter the input to only keep allowed characters
      const matches = val.match(allowedRegex);
      const sanitizedValue = matches ? matches.join('') : '';

      if (!controlledText) setTextState(sanitizedValue);
      if (typeof onTextChange === 'function') onTextChange(sanitizedValue);
    },
    [controlledText, onTextChange, defaultRestrictions]
  );

  /**
   * Handles key down events to prevent restricted characters from being typed.
   * @param {KeyboardEvent} e - The keyboard event.
   * @returns {boolean} True if the key should be prevented.
   */
  const handleKeyDown = useCallback(
    (e) => {
      // Always allow control and modifier keys to work normally.
      const isControlKey =
        e.key === 'Backspace' ||
        e.key === 'Delete' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight' ||
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown' ||
        e.key === 'Tab' ||
        e.key === 'Enter' ||
        e.key === 'Escape' ||
        e.metaKey || // Cmd key on Mac.
        e.ctrlKey || // Ctrl key on Windows/Linux.
        e.altKey; // Alt key.

      if (isControlKey) {
        return false; // Allow special keys to work normally.
      }

      // Get the regex pattern for allowed characters
      const allowedPattern = getRegexFromRestrictions(defaultRestrictions);

      // Create regex to test if the key is allowed
      // eslint-disable-next-line
      const allowedRegex = new RegExp(`[${allowedPattern}]`);

      // Check if the key is allowed
      if (!allowedRegex.test(e.key)) {
        return true; // Prevent the key.
      }

      return false; // Allow the key.
    },
    [defaultRestrictions]
  );

  const textMap = {
    bag: 'Personalise the back your gift bag',
    box: 'Personalise the back your gift box',
    card: 'Personalise the front your gift card'
  };

  return {
    enabled,
    text,
    textMap,
    handleToggle,
    handleChange,
    handleKeyDown,
    formatPrice
  };
}
